import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import TransportMapPanel from './TransportMapPanel';
import { getRunColor } from './runColors';

const ORANGE = '#ee7203';

/** Same run-numbering contract as TransportMapPanel: Run 1 = vehicle.entries,
 *  Run 2+ = vehicle.runs[], colour-matched via the shared getRunColor() helper
 *  so the printed table headers and the printed map's route lines always agree. */
const getVehicleRuns = (vehicle) => {
  const runs = [
    { label: 'Run 1', color: getRunColor(1), entries: vehicle.entries || [] },
    ...(vehicle.runs || []).map((r, i) => {
      const runNumber = r.run_number || i + 2;
      return { label: `Run ${runNumber}`, color: getRunColor(runNumber), entries: r.entries || [] };
    }),
  ];
  return runs.filter((r) => r.entries.length > 0);
};

const calcWeight = (entries) => entries.reduce((sum, e) => sum + (parseFloat(e.weight) || 0), 0);

/**
 * Redesigned print output for the New Transport UI — one full A4 page PER
 * vehicle: a coloured, run-grouped run/entry table at the top, then that
 * vehicle's live route map (same TransportMapPanel used on-screen, so run
 * colours match exactly) underneath.
 *
 * Mounted off-screen (position:fixed, far left) rather than display:none
 * while NOT printing — this is deliberate: display:none collapses the map
 * containers to 0×0, which breaks Leaflet's initial size calculation and is
 * why maps would otherwise render blank. Off-screen fixed positioning keeps
 * real pixel dimensions the whole time, so tiles load normally in the
 * background before the user even opens the print dialog.
 * Rendered via a React portal directly onto document.body — this keeps it
 * completely outside the app's own layout tree, so hiding "everything else"
 * for print (via display:none, not just visibility:hidden) can't leave a
 * blank leading page, and our content can stay in normal static flow
 * (required for multi-page pagination to work at all — position:absolute
 * content does NOT reliably paginate across multiple printed pages in
 * Chromium, which is what caused the original "only the first couple of
 * vehicles print" bug).
 */
const PrintTransportPages = ({ vehicles, selectedDate, basePostcode }) => {
  return createPortal(
    <div className="print-transport-pages" data-testid="print-transport-pages">
      <style>{`
        @media screen {
          .print-transport-pages {
            position: fixed;
            top: 0;
            left: -99999px;
            width: 186mm;
            z-index: -1;
          }
        }
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          body > *:not(.print-transport-pages) { display: none !important; }
          .print-transport-pages { position: static; left: 0; top: 0; width: 100%; z-index: auto; }
          .print-vehicle-page { page-break-after: always; }
          .print-vehicle-page:last-child { page-break-after: auto; }
        }
        .print-vehicle-page {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          color: #1f2937;
        }
        .print-page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: ${ORANGE};
          color: #fff;
          border-radius: 10px;
          padding: 10px 16px;
          margin-bottom: 10px;
        }
        .print-page-reg { font-size: 20px; font-weight: 800; letter-spacing: 0.02em; }
        .print-page-driver { font-size: 12px; opacity: 0.9; margin-top: 2px; }
        .print-page-date-box { text-align: right; }
        .print-page-date { font-size: 13px; font-weight: 700; }
        .print-page-totals { font-size: 12px; opacity: 0.9; margin-top: 2px; }
        .print-run-block { margin-bottom: 8px; border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; }
        .print-run-title { color: #fff; font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; padding: 5px 10px; }
        .print-run-table { width: 100%; border-collapse: collapse; font-size: 11px; }
        .print-run-table th { text-align: left; padding: 5px 10px; background: #f9fafb; font-size: 9.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
        .print-run-table td { padding: 5px 10px; border-bottom: 1px solid #f3f4f6; }
        .print-run-table td.num { text-align: right; font-weight: 700; }
        .print-run-total-row td { font-weight: 800; background: #fff7ed; border-top: 2px solid ${ORANGE}; }
        .print-map-wrap { position: relative; width: 100%; height: 92mm; border: 2px solid ${ORANGE}; border-radius: 10px; overflow: hidden; margin-top: 6px; }
      `}</style>

      {vehicles.map((vehicle) => {
        const runs = getVehicleRuns(vehicle);
        const totalWeight = runs.reduce((s, r) => s + calcWeight(r.entries), 0);
        return (
          <div className="print-vehicle-page" key={vehicle.id} data-testid={`print-page-${vehicle.id}`}>
            <div className="print-page-header">
              <div>
                <div className="print-page-reg">{vehicle.vehicle_registration || 'Unassigned'}</div>
                <div className="print-page-driver">
                  {vehicle.driver_name || 'No driver assigned'}{vehicle.driver_phone ? ` · ${vehicle.driver_phone}` : ''}
                  {vehicle.vehicle_type ? ` · ${vehicle.vehicle_type}` : ''}
                </div>
              </div>
              <div className="print-page-date-box">
                <div className="print-page-date">{format(selectedDate, 'EEEE d MMMM yyyy')}</div>
                <div className="print-page-totals">{totalWeight.toFixed(2)}T · {(vehicle.total_miles || 0).toFixed(1)} mi</div>
              </div>
            </div>

            {runs.length === 0 ? (
              <div style={{ padding: '10px 4px', fontSize: 12, color: '#9ca3af' }}>No entries planned for this vehicle.</div>
            ) : runs.map((run) => {
              const runTotal = calcWeight(run.entries);
              return (
                <div key={run.label} className="print-run-block">
                  <div className="print-run-title" style={{ background: run.color }}>{run.label}</div>
                  <table className="print-run-table">
                    <thead>
                      <tr>
                        <th style={{ width: '12%' }}>Time</th>
                        <th style={{ width: '34%' }}>Customer</th>
                        <th style={{ width: '18%' }}>Postcode</th>
                        <th style={{ width: '20%' }}>Order #</th>
                        <th style={{ width: '16%', textAlign: 'right' }}>Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {run.entries.map((entry, idx) => (
                        <tr key={entry.id || idx} style={{ background: idx % 2 === 0 ? '#ffffff' : '#fef8f2' }}>
                          <td>{entry.time_slot || '—'}</td>
                          <td>{entry.customer_name || 'No customer'}</td>
                          <td>{entry.postcode || '—'}</td>
                          <td>{entry.order_number || (entry.order_numbers || []).join(', ') || '—'}</td>
                          <td className="num">{entry.weight ? `${parseFloat(entry.weight).toFixed(2)}T` : '—'}</td>
                        </tr>
                      ))}
                      <tr className="print-run-total-row">
                        <td colSpan={4} style={{ textAlign: 'right' }}>Run Total</td>
                        <td className="num">{runTotal.toFixed(2)}T</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}

            <div className="print-map-wrap">
              <TransportMapPanel basePostcode={basePostcode} vehicle={vehicle} className="w-full h-full" />
            </div>
          </div>
        );
      })}
    </div>,
    document.body
  );
};

export default PrintTransportPages;
